import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { categoricalColor, CHART_TOOLTIP_STYLE } from '@/lib/chartPalette';

interface Series {
  key: string;
  label: string;
}

/** Matches the `fontSize` handed to both axes below. */
const TICK_FONT_SIZE = 12;

/**
 * Recharts' own DEFAULT_Y_AXIS_WIDTH, kept as a floor. Every chart whose
 * labels already fit inside 60px (all the count-only ones) therefore keeps
 * exactly the layout it has today, and only a chart that needs more room
 * takes more.
 */
const DEFAULT_Y_AXIS_WIDTH = 60;

/** Breathing room between the longest label and the plot area. */
const TICK_GAP = 8;

/** Fallback per-character width when canvas measurement is unavailable. */
const APPROX_CHAR_WIDTH = 7;

let measuringContext: CanvasRenderingContext2D | null | undefined;

/**
 * Measures a tick label in the font the axis actually renders it in. A canvas
 * context measures the same font metrics the SVG text will use, which a
 * character count cannot: "UGX 1,111,111" and "UGX 0,000,000" have the same
 * length and different widths.
 */
function measureTickLabel(label: string): number {
  if (measuringContext === undefined) {
    measuringContext = document.createElement('canvas').getContext('2d');
  }

  if (measuringContext == null) {
    return label.length * APPROX_CHAR_WIDTH;
  }

  const fontFamily = window.getComputedStyle(document.body).fontFamily || 'sans-serif';
  measuringContext.font = `${TICK_FONT_SIZE}px ${fontFamily}`;

  return measuringContext.measureText(label).width;
}

/**
 * The widest label the y-axis can end up showing, formatted exactly as the
 * axis will format it.
 *
 * The top tick is not simply the largest datum: Recharts rounds it up to a
 * round number, so a 750,000 bar tops out at a 1,000,000 tick, one character
 * wider. Measuring the next power of ten above the data is a cheap upper
 * bound on that rounding (it can over-reserve by a character, never under).
 * A negative datum anywhere adds the minus sign, which the formatter places
 * itself: `formatCurrencyAmount` renders "-UGX 600,000", not "UGX -600,000".
 */
function widestTickLabel(
  data: Array<Record<string, string | number>>,
  series: Series[],
  valueFormatter?: (value: number) => string,
): string {
  let largestMagnitude = 0;
  let hasNegative = false;

  for (const row of data) {
    for (const s of series) {
      const value = row[s.key];
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        continue;
      }
      if (value < 0) {
        hasNegative = true;
      }
      largestMagnitude = Math.max(largestMagnitude, Math.abs(value));
    }
  }

  const bound = largestMagnitude === 0 ? 0 : 10 ** Math.ceil(Math.log10(largestMagnitude + 1));
  const worstCase = hasNegative ? -bound : bound;

  return valueFormatter ? valueFormatter(worstCase) : String(worstCase);
}

export function MonthlyBarChart({
  data,
  series,
  valueFormatter,
}: {
  data: Array<Record<string, string | number>>;
  series: Series[];
  /**
   * Optional. Formats axis ticks and tooltip values, e.g. money via
   * `formatCurrencyAmount`. Additive and opt-in: omitting it keeps the
   * previous plain-number rendering, so existing consumers of this chart
   * are unaffected.
   */
  valueFormatter?: (value: number) => string;
}) {
  /*
    Recharts' default y-axis width is a fixed 60px (DEFAULT_Y_AXIS_WIDTH,
    unchanged between v2 and v3), and a formatted money tick overflows it:
    "UGX 750,000" rendered as "X 750,000", with the currency code sheared off
    the left. No fixed number can replace it either, because valueFormatter
    belongs to the caller and the currency code varies per group, so the
    label's width has to be measured rather than assumed. An "FCFA"-prefixed
    billion needs more than twice the default.

    Recharts 3 does offer `width="auto"`, which measures the rendered ticks
    itself, and it is NOT usable here: it only completes its measurement if
    the YAxis re-renders at least once after mount, and YAxis is wrapped in
    React.memo (see util/propsAreEqual). With props that are stable across
    renders, which is every caller that passes no valueFormatter, the
    measurement never runs and the y-axis is dropped from the DOM entirely,
    ticks and all. It appeared to work on the MRR movement chart only because
    MrrMovementChart happens to pass a fresh closure on every render, and
    tickFormatter is not on Recharts' shallow-compare allowlist, so the memo
    bails out by accident. Anything that later memoised that closure would
    silently delete the axis, so this measures the width itself instead.
  */
  const yAxisWidth = useMemo(
    () =>
      Math.max(
        DEFAULT_Y_AXIS_WIDTH,
        Math.ceil(measureTickLabel(widestTickLabel(data, series, valueFormatter))) + TICK_GAP,
      ),
    [data, series, valueFormatter],
  );

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
        <YAxis
          stroke="hsl(var(--muted-foreground))"
          fontSize={TICK_FONT_SIZE}
          tickLine={false}
          axisLine={false}
          tickFormatter={valueFormatter}
          width={yAxisWidth}
        />
        <Tooltip
          contentStyle={CHART_TOOLTIP_STYLE}
          cursor={{ fill: 'hsl(var(--muted))' }}
          formatter={valueFormatter ? (value: number) => valueFormatter(value) : undefined}
          // Recharts v2 had no default itemSorter, so tooltip rows followed the
          // order the series were declared in. v3 defaults it to 'name', which
          // alphabetises them: MRR movement then reads "Churned, New,
          // Retained" even though its caption describes "new minus churned
          // plus retained". Sorting by declared position restores the reading
          // order the callers and their captions assume.
          itemSorter={(item) => series.findIndex((s) => s.key === item.dataKey)}
        />
        {/*
          itemSorter={null} keeps the legend in payload order, which for bars is
          the order the <Bar> children are declared. Recharts v3 defaults it to
          'value' (alphabetical by label), which v2 did not do, and that detaches
          the legend from both the declared series order and the fixed-order
          hue assignment in chartPalette.
        */}
        {series.length > 1 && <Legend iconType="circle" iconSize={8} itemSorter={null} />}
        {series.map((s, index) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.label}
            fill={categoricalColor(index)}
            radius={[4, 4, 0, 0]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
